import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, Clock, X, RefreshCw, Stethoscope, MessageSquare, AlertCircle, CalendarPlus, Sparkles, Video } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { parseISO, isBefore } from "date-fns";
import { useAuditLog } from "@/hooks/useAuditLog";
import { getBrowserTimezone, getTimezoneAbbr, formatInTz } from "@/lib/timezone";

interface AppointmentWithProvider {
  id: string;
  provider_id: string;
  start_time: string;
  end_time: string;
  status: string;
  notes: string | null;
  meeting_url: string | null;
  call_type: string;
  provider_first_name?: string | null;
  provider_last_name?: string | null;
}

const statusConfig: Record<string, { label: string; class: string }> = {
  scheduled: { label: "Upcoming", class: "bg-primary/10 text-primary border-primary/20" },
  confirmed: { label: "Confirmed", class: "bg-primary/10 text-primary border-primary/20" },
  cancelled: { label: "Cancelled", class: "bg-muted text-muted-foreground border-border line-through" },
  completed: { label: "Completed", class: "bg-green-100 text-green-800 border-green-200" },
  rescheduled: { label: "Rescheduled", class: "bg-blue-100 text-blue-800 border-blue-200" },
  no_show: { label: "No-Show", class: "bg-red-100 text-red-800 border-red-200" },
};

const UpcomingAppointments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [appointments, setAppointments] = useState<AppointmentWithProvider[]>([]);
  const [cancelDialog, setCancelDialog] = useState<AppointmentWithProvider | null>(null);
  const [rescheduleDialog, setRescheduleDialog] = useState<AppointmentWithProvider | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [hasAssessment, setHasAssessment] = useState(false);
  const { logAccess } = useAuditLog();

  const tz = useMemo(() => getBrowserTimezone(), []);
  const tzAbbr = useMemo(() => getTimezoneAbbr(tz), [tz]);

  useEffect(() => {
    if (!user) return;
    fetchAppointments();
    // Check if user has completed an assessment
    supabase
      .from("health_assessments")
      .select("id")
      .eq("user_id", user.id)
      .limit(1)
      .then(({ data }) => setHasAssessment((data?.length ?? 0) > 0));
  }, [user]);

  const fetchAppointments = async () => {
    if (!user) return;

    const { data } = await supabase
      .from("appointments")
      .select("id, provider_id, start_time, end_time, status, notes, meeting_url, call_type")
      .eq("patient_id", user.id)
      .order("start_time", { ascending: true });

    if (!data || data.length === 0) {
      setAppointments([]);
      return;
    }

    // Fetch provider profiles via public view (hides PII)
    const providerIds = [...new Set(data.map((a: any) => a.provider_id))];
    const { data: profiles } = await supabase
      .from("provider_profiles_public" as any)
      .select("user_id, first_name, last_name")
      .in("user_id", providerIds);

    const profileMap = new Map(profiles?.map((p: any) => [p.user_id, p]) || []);

    setAppointments(
      data.map((a: any) => {
        const profile = profileMap.get(a.provider_id);
        return {
          ...a,
          provider_first_name: profile?.first_name,
          provider_last_name: profile?.last_name,
        };
      })
    );
    logAccess({ action: "view_appointments", resource_type: "appointment", metadata: { count: data.length } });
  };

  const cancelAppointment = async (appointment: AppointmentWithProvider) => {
    setIsCancelling(true);
    const { error } = await supabase
      .from("appointments")
      .update({ status: "cancelled" })
      .eq("id", appointment.id);

    if (error) {
      toast({ title: "Cancel failed", description: error.message, variant: "destructive" });
    } else {
      logAccess({ action: "cancel_appointment", resource_type: "appointment", resource_id: appointment.id });

      // Notify provider via email only. We intentionally do NOT insert into
      // the notifications table client-side: notifications RLS only allows a
      // user to insert rows for themselves, so a patient inserting a row for
      // their provider would always be rejected and pollute the admin error
      // log. The provider sees the cancellation via the appointment status
      // realtime channel + email.
      try {
        const providerDisplayName = [appointment.provider_first_name, appointment.provider_last_name]
          .filter(Boolean)
          .join(" ") || "your patient";

        const { data: patientProfile } = await supabase
          .from("profiles")
          .select("first_name, last_name")
          .eq("user_id", user!.id)
          .maybeSingle();
        const patientName = [patientProfile?.first_name, patientProfile?.last_name]
          .filter(Boolean)
          .join(" ") || "A patient";

        const { data: emails } = await supabase.rpc("get_user_emails");
        const providerEmail = emails?.find((e: any) => e.user_id === appointment.provider_id)?.email;
        if (providerEmail) {
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-cancelled",
              recipientEmail: providerEmail,
              idempotencyKey: `appt-cancelled-patient-${appointment.id}`,
              templateData: {
                recipientName: appointment.provider_first_name || providerDisplayName,
                patientName,
                cancelledBy: "patient",
                appointmentDate: formatInTz(appointment.start_time, tz, "MMMM d, yyyy"),
                appointmentTime: `${formatInTz(appointment.start_time, tz, "h:mm a")} – ${formatInTz(appointment.end_time, tz, "h:mm a")} ${tzAbbr}`,
              },
            },
          });
        }
      } catch (e) {
        console.error("Failed to notify provider of patient cancellation", e);
      }

      toast({ title: "Appointment cancelled" });
      fetchAppointments();
    }
    setIsCancelling(false);
    setCancelDialog(null);
  };

  const confirmReschedule = async (appointment: AppointmentWithProvider) => {
    setIsRescheduling(true);
    const { error } = await supabase
      .from("appointments")
      .update({ status: "rescheduled" })
      .eq("id", appointment.id);

    if (error) {
      toast({ title: "Reschedule failed", description: error.message, variant: "destructive" });
    } else {
      logAccess({ action: "reschedule_appointment", resource_type: "appointment", resource_id: appointment.id });

      // Notify provider via email that the patient initiated a reschedule.
      // We intentionally skip the cross-user notifications insert here — RLS
      // would reject it and pollute the admin error log. Provider sees the
      // status change via the appointments realtime channel.
      try {
        const { data: patientProfile } = await supabase
          .from("profiles")
          .select("first_name, last_name")
          .eq("user_id", user!.id)
          .maybeSingle();
        const patientName = [patientProfile?.first_name, patientProfile?.last_name]
          .filter(Boolean)
          .join(" ") || "A patient";

        const { data: emails } = await supabase.rpc("get_user_emails");
        const providerEmail = emails?.find((e: any) => e.user_id === appointment.provider_id)?.email;
        if (providerEmail) {
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-rescheduled",
              recipientEmail: providerEmail,
              idempotencyKey: `appt-rescheduled-patient-${appointment.id}`,
              templateData: {
                recipientName: appointment.provider_first_name || "Provider",
                patientName,
                rescheduledBy: "patient",
                appointmentDate: formatInTz(appointment.start_time, tz, "MMMM d, yyyy"),
                appointmentTime: `${formatInTz(appointment.start_time, tz, "h:mm a")} – ${formatInTz(appointment.end_time, tz, "h:mm a")} ${tzAbbr}`,
              },
            },
          });
        }
      } catch (e) {
        console.error("Failed to notify provider of patient reschedule", e);
      }

      toast({ title: "Appointment rescheduled", description: "Pick a new time below." });
      navigate("/book");
    }
    setIsRescheduling(false);
    setRescheduleDialog(null);
  };

  const upcoming = appointments.filter(
    (a) => a.status === "scheduled" && !isBefore(parseISO(a.end_time), new Date())
  );
  const past = appointments.filter(
    (a) => a.status !== "scheduled" || isBefore(parseISO(a.end_time), new Date())
  );

  // Surface recently-cancelled future appointments as a rebook prompt
  const extractCancelReason = (notes: string | null): string | null => {
    if (!notes) return null;
    const matches = [...notes.matchAll(/\[Cancelled [^\]]+\]\s*Reason:\s*([^\n]+)/g)];
    return matches.length ? matches[matches.length - 1][1].trim() : null;
  };
  const rebookable = appointments
    .filter((a) => (a.status === "cancelled" || a.status === "rescheduled") && !isBefore(parseISO(a.start_time), new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)))
    .sort((a, b) => parseISO(b.start_time).getTime() - parseISO(a.start_time).getTime())
    .slice(0, 1);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl text-foreground">Your Appointments</h2>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={!hasAssessment}
          onClick={() => navigate("/book")}
          title={!hasAssessment ? "Complete your health assessment first" : undefined}
        >
          <CalendarDays size={14} /> Book Appointment
        </Button>
      </div>

      {appointments.length === 0 && (
        <Card className="border-primary/15 bg-gradient-to-br from-primary/5 via-card to-secondary/30 overflow-hidden">
          <CardContent className="p-10 text-center">
            <div className="relative mx-auto mb-5 w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-primary/10 animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center ring-4 ring-primary/5">
                <CalendarPlus size={32} className="text-primary" strokeWidth={1.75} />
              </div>
              <Sparkles size={14} className="absolute -top-1 -right-1 text-primary/60" />
            </div>
            <h3 className="font-display text-xl text-foreground mb-2">
              {hasAssessment ? "No appointments yet" : "Let's get you started"}
            </h3>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto leading-relaxed">
              {hasAssessment
                ? "Book a consultation with a licensed provider to get started on your peptide therapy plan."
                : "Complete your quick health assessment so a licensed provider can review your goals before your first consultation."}
            </p>
            <Button
              size="lg"
              onClick={() => navigate(hasAssessment ? "/book" : "/get-started")}
              className="gap-2 shadow-sm"
            >
              <CalendarDays size={16} />
              {hasAssessment ? "Book your first appointment" : "Start health assessment"}
            </Button>
          </CardContent>
        </Card>
      )}

      {rebookable.length > 0 && hasAssessment && upcoming.length === 0 && (
        <div className="space-y-3">
          {rebookable.map((a) => {
            const reason = extractCancelReason(a.notes);
            return (
              <Card key={`rebook-${a.id}`} className="border-destructive/30 bg-destructive/5">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0 mt-0.5">
                      <AlertCircle size={18} className="text-destructive" />
                    </div>
                    <div className="flex-1 space-y-1.5">
                      <p className="font-medium text-foreground text-sm">
                        Your {formatInTz(a.start_time, tz, "MMMM d")} appointment was {a.status === "rescheduled" ? "rescheduled" : "cancelled"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Originally with Dr. {a.provider_last_name || a.provider_first_name || "your provider"} at {formatInTz(a.start_time, tz, "h:mm a")} {tzAbbr}
                      </p>
                      {reason && (
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">Reason:</span> {reason}
                        </p>
                      )}
                      <div className="pt-2">
                        <Button size="sm" className="gap-1.5" onClick={() => navigate("/book")}>
                          <CalendarDays size={14} /> Rebook Appointment
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="space-y-3">
          {upcoming.map((a) => (
            <Card key={a.id} className="border-primary/20 bg-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Stethoscope size={18} className="text-primary" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-foreground text-sm">
                          {formatInTz(a.start_time, tz, "EEEE, MMMM d, yyyy")}
                        </p>
                        {a.call_type === "video" ? (
                          <Badge variant="outline" className="gap-1 text-[10px] bg-primary/10 text-primary border-primary/20">
                            <Video size={10} /> Live video
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1 text-[10px] bg-secondary/40 text-foreground border-border">
                            <MessageSquare size={10} /> Async messaging
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <Clock size={12} className="inline mr-1" />
                        {formatInTz(a.start_time, tz, "h:mm a")} – {formatInTz(a.end_time, tz, "h:mm a")} {tzAbbr}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        with Dr. {a.provider_last_name || a.provider_first_name || "Provider"}
                      </p>
                      {a.meeting_url && a.call_type === "video" ? (
                        <a
                          href={a.meeting_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline mt-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Video size={12} />
                          Join video call
                        </a>
                      ) : a.meeting_url ? (
                        <a
                          href={a.meeting_url}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline mt-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MessageSquare size={12} />
                          Open secure inbox
                        </a>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-xs"
                      onClick={() => setRescheduleDialog(a)}
                    >
                      <RefreshCw size={12} /> Reschedule
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-xs text-destructive hover:text-destructive"
                      onClick={() => setCancelDialog(a)}
                    >
                      <X size={12} /> Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {past.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-4">Past & Cancelled</p>
          {past.map((a) => {
            const config = statusConfig[a.status] || statusConfig.completed;
            const canRebook = (a.status === "cancelled" || a.status === "rescheduled") && hasAssessment;
            return (
              <Card key={a.id} className="border-border bg-card/60">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="space-y-0.5 min-w-0">
                    <p className="text-sm text-muted-foreground">
                      {formatInTz(a.start_time, tz, "MMM d, yyyy")} · {formatInTz(a.start_time, tz, "h:mm a")} {tzAbbr}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Dr. {a.provider_last_name || a.provider_first_name || "Provider"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.call_type === "video" ? (
                      <Badge variant="outline" className="gap-1 text-[10px] bg-primary/5 text-primary/80 border-primary/20">
                        <Video size={10} /> Video
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 text-[10px] bg-secondary/30 text-muted-foreground border-border">
                        <MessageSquare size={10} /> Async
                      </Badge>
                    )}
                    <Badge variant="outline" className={`text-xs capitalize ${config.class}`}>
                      {config.label}
                    </Badge>
                    {canRebook && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-xs h-7"
                        onClick={() => navigate("/book")}
                      >
                        <CalendarDays size={12} /> Rebook
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Cancel confirmation dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={(open) => { if (!open) setCancelDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Appointment?</DialogTitle>
            <DialogDescription>
              {cancelDialog && (
                <>
                  This will cancel your {formatInTz(cancelDialog.start_time, tz, "MMMM d")} appointment
                  at {formatInTz(cancelDialog.start_time, tz, "h:mm a")} {tzAbbr} with Dr.{" "}
                  {cancelDialog.provider_last_name || cancelDialog.provider_first_name}. This action cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelDialog(null)}>Keep Appointment</Button>
            <Button
              variant="destructive"
              onClick={() => cancelDialog && cancelAppointment(cancelDialog)}
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling..." : "Cancel Appointment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule confirmation dialog */}
      <Dialog open={!!rescheduleDialog} onOpenChange={(open) => { if (!open) setRescheduleDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule Appointment?</DialogTitle>
            <DialogDescription>
              {rescheduleDialog && (
                <>
                  Your current appointment on {formatInTz(rescheduleDialog.start_time, tz, "MMMM d")} at{" "}
                  {formatInTz(rescheduleDialog.start_time, tz, "h:mm a")} {tzAbbr} with Dr.{" "}
                  {rescheduleDialog.provider_last_name || rescheduleDialog.provider_first_name} will be cancelled
                  and you'll be taken to book a new time. Would you like to proceed?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRescheduleDialog(null)}>Keep Current Appointment</Button>
            <Button
              onClick={() => rescheduleDialog && confirmReschedule(rescheduleDialog)}
              disabled={isRescheduling}
            >
              {isRescheduling ? "Rescheduling..." : "Reschedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UpcomingAppointments;
